import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bot, Sparkles, Activity, MessageSquare, Shield } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { aiUsageByPlan, platformKpis } from "@/data/platform";
import { toast } from "sonner";

const personas = [
  { id: "admin", name: "Admin Helper", desc: "Operational queries for tenant admins", enabled: true },
  { id: "hr", name: "HR Co-pilot", desc: "Workforce policies & employee queries", enabled: true },
  { id: "supervisor", name: "Supervisor Aide", desc: "Site-level attendance summaries", enabled: true },
  { id: "employee", name: "Employee Companion", desc: "Self-service from mobile app", enabled: false },
];

const topQueries = [
  "How many employees missed check-in today?",
  "Summarize today's attendance by department",
  "Show pending correction requests",
  "List attendance exceptions for Site A",
  "How do I configure a geofence?",
];

export default function AIControl() {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-4">
        <Card className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground"><Activity className="h-3 w-3" /> Interactions today</div><div className="mt-1 font-display text-2xl font-bold">{platformKpis.aiInteractionsToday.toLocaleString()}</div><div className="text-[11px] text-success">+18% vs yesterday</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground"><Bot className="h-3 w-3" /> Active tenants</div><div className="mt-1 font-display text-2xl font-bold">8 / 10</div><div className="text-[11px] text-muted-foreground">using AI Assistant</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground"><MessageSquare className="h-3 w-3" /> Avg session</div><div className="mt-1 font-display text-2xl font-bold">3.4</div><div className="text-[11px] text-muted-foreground">turns per chat</div></Card>
        <Card className="p-4"><div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground"><Sparkles className="h-3 w-3" /> Active personas</div><div className="mt-1 font-display text-2xl font-bold">{personas.filter(p => p.enabled).length} / {personas.length}</div></Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader><CardTitle className="text-base">Usage by plan</CardTitle><CardDescription>AI interactions distributed by tier</CardDescription></CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={aiUsageByPlan}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="plan" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="interactions" fill="hsl(var(--accent))" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Top queries</CardTitle><CardDescription>Most asked across tenants</CardDescription></CardHeader>
          <CardContent className="space-y-2">
            {topQueries.map((q, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg border bg-secondary/30 p-2.5">
                <Badge variant="outline" className="text-[10px]">{i + 1}</Badge>
                <div className="text-xs">{q}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Personas & access</CardTitle><CardDescription>Toggle AI availability per audience</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            {personas.map(p => (
              <div key={p.id} className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-xs text-muted-foreground">{p.desc}</div>
                </div>
                <Switch defaultChecked={p.enabled} onCheckedChange={(v) => toast.success(`${p.name} ${v ? "enabled" : "disabled"}`)} />
              </div>
            ))}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Plan-tier access</CardTitle><CardDescription>Enable AI per subscription tier</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            {[
              { p: "Basic", on: false, hint: "Disabled by default" },
              { p: "Pro", on: true, hint: "Limited model · 200/day" },
              { p: "Premium", on: true, hint: "Full model · 2,000/day" },
              { p: "Enterprise", on: true, hint: "Premium model · Unlimited" },
            ].map(t => (
              <div key={t.p} className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <div className="text-sm font-medium">{t.p}</div>
                  <div className="text-xs text-muted-foreground">{t.hint}</div>
                </div>
                <Switch defaultChecked={t.on} onCheckedChange={(v) => toast.info(`${t.p}: AI ${v ? "on" : "off"}`)} />
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Shield className="h-4 w-4" /> AI defaults & governance</CardTitle><CardDescription>Platform-level chatbot configuration</CardDescription></CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="grid gap-1.5">
            <Label>Default model</Label>
            <Select defaultValue="gpt-4o-mini">
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gpt-4o-mini">GPT-4o-mini (fast)</SelectItem>
                <SelectItem value="gpt-4o">GPT-4o (balanced)</SelectItem>
                <SelectItem value="claude-3-5">Claude 3.5 Sonnet</SelectItem>
                <SelectItem value="gemini-1-5">Gemini 1.5 Pro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1.5"><Label>Daily cap per tenant</Label><Input type="number" defaultValue={2000} /></div>
          <div className="grid gap-1.5 md:col-span-2">
            <Label>Default greeting</Label>
            <Textarea defaultValue="Hi, I'm GeoKerja's assistant. Ask me about attendance, sites, or governance." />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><div className="text-sm font-medium">Audit AI conversations</div><div className="text-xs text-muted-foreground">Store chat logs for compliance review</div></div>
            <Switch defaultChecked />
          </div>
          <div className="flex items-center justify-between rounded-lg border p-3">
            <div><div className="text-sm font-medium">Human escalation</div><div className="text-xs text-muted-foreground">Route to support when AI is uncertain</div></div>
            <Switch defaultChecked />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end"><Button onClick={() => toast.success("AI settings saved")}>Save AI settings</Button></div>
    </div>
  );
}
